<template>
  <div>
    <div class="d-flex" style="width: 520px">
      <v-text-field
        v-model="search"
        class="fix-google-chrome mr-2"
        label="รหัส - ชื่อ Device"
        prepend-inner-icon="mdi-magnify"
        clearable
        outlined
        dense
        hide-details
        @input="handleSearch"
      ></v-text-field>
      <v-autocomplete
        v-model="filterArea"
        class="fix-google-chrome"
        label="พื้นที่"
        :items="areas"
        item-text="title_th"
        item-value="area_uuid"
        outlined
        dense
        hide-details
        clearable
        @change="handleFilterArea"
        :loading="loadingFilterArea"
      >
      </v-autocomplete>
      <v-btn class="ml-2" icon @click="handleRefresh">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <v-data-table
      class="mt-4 with-borders"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :loading="loading"
      :headers="headers"
      :items="flattenedRows"
      :options.sync="optionsHardware"
      :server-items-length="authorityDeviceSummary.total"
      :items-per-page="10"
      :footer-props="{
        itemsPerPageOptions: [10, 25, 50, 100],
        itemsPerPageText: 'Rows per page:',
      }"
      must-sort
    >
      <template v-slot:body="{ items }">
        <tbody>
          <tr v-if="!items.length">
            <td style="text-align: center" colspan="4">No data available</td>
          </tr>
          <tr v-for="(item, index) in items" :key="index">
            <!-- Hardware -->
            <td
              :style="`vertical-align: ${
                item.hardwareRowSpan > 1 ? 'top' : 'middle'
              }; padding: 10px 15px`"
              v-if="item.showHardware"
              :rowspan="item.hardwareRowSpan"
            >
              {{ item.hardwareCode }} - {{ item.hardwareTitle }}
            </td>

            <!-- Employee -->
            <td
              :style="`vertical-align: ${
                item.employeeRowSpan > 1 ? 'top' : 'middle'
              }; padding: 10px 15px`"
              v-if="item.showEmployee"
              :rowspan="item.employeeRowSpan"
            >
              {{ item.employeeName }} ({{ item.employeeCode }})<br />
              <span class="grey--text caption">{{
                item.organizationName
              }}</span>
            </td>

            <!-- Authority -->
            <td>
              <div>{{ item.authority }}</div>
              <div class="caption grey--text">
                {{ item.accessControlStartTime }} -
                {{ item.accessControlEndTime }}
              </div>
            </td>

            <!-- Timezone -->
            <td>
              <div class="d-flex justify-space-between align-center">
                <div>{{ item.timezone }}</div>
                <div>
                  <v-btn
                    text
                    color="error"
                    @click.stop="onDeleteEmployee(item)"
                    :disabled="
                      uiCtrl &&
                      !uiCtrl.isEnable('btn:::authority_employee:delete')
                    "
                    >ลบ</v-btn
                  >
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
    <ConfirmDialog
      :dialog="confirmDeleteEmployee"
      :loading="confirmDeleteEmployeeLoading"
      :dialogData="confirmDeleteEmployeeData"
      @cancelConfirmDialog="cancelConfirmDeleteEmployee"
      @submitConfirmDialog="submitConfirmDeleteEmployee"
    />
  </div>
</template>

<script>
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'

export default {
  components: {
    ConfirmDialog,
  },
  props: {
    dialogCheckPermissions: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      search: '',
      filterArea: null,
      sortBy: ['hardwareCode'],
      sortDesc: [false],
      loading: false,
      loadingFilterArea: false,
      headers: [
        {
          text: 'Device',
          align: 'start',
          value: 'hardwareCode',
          width: '20%',
        },
        {
          text: 'พนักงานที่อยู่ใน Device',
          value: 'employeeName',
          width: '30%',
          sortable: false,
        },
        {
          text: 'กลุ่มสิทธิ์',
          value: 'authority',
          width: '25%',
          sortable: false,
        },
        {
          text: 'ช่วงเวลา',
          value: 'timezone',
          width: '25%',
          sortable: false,
        },
      ],
      optionsHardware: {},
      confirmDeleteEmployee: false,
      confirmDeleteEmployeeLoading: false,
      confirmDeleteEmployeeData: {},
    }
  },
  computed: {
    uiCtrl() {
      return this.$store.state.uiCtrl
    },
    areas() {
      return this.$store.state.area.areas
    },
    authorityDeviceSummary() {
      return this.$store.state.accessAuthority.authorityDeviceSummary
    },
    flattenedRows() {
      const result = []
      const hardwares = this.authorityDeviceSummary.hardwares || []

      hardwares.forEach((hardware) => {
        const { hardware_code, title_th } = hardware
        const employees = hardware.employees || []

        employees.forEach((emp, empIndex) => {
          const employee_uuid = emp.employee_uuid
          const employeeCode = emp.employee_code || ''
          const employeeName = `${emp.firstname_th} ${emp.lastname_th}`
          const organizationName = emp.organization?.title_th || ''
          const authorities = emp.authorities || []

          authorities.forEach((auth, authIndex) => {
            result.push({
              hardwareCode: hardware_code,
              hardwareTitle: title_th,
              showHardware: empIndex === 0 && authIndex === 0,
              hardwareRowSpan: employees.reduce(
                (total, e) => total + (e.authorities?.length || 1),
                0
              ),
              employee_uuid,
              employeeCode,
              employeeName,
              organizationName,
              showEmployee: authIndex === 0,
              employeeRowSpan: authorities.length || 1,
              authority: `${auth.authority.authority_code} - ${auth.authority.title_th}`,
              authority_uuid: auth.authority.authority_uuid,
              timezone: `${auth.timezone.timezone_code} - ${auth.timezone.title_th}`,
              accessControlStartTime: auth.authority.access_control_start_time
                ? moment
                    .utc(auth.authority.access_control_start_time)
                    .format('DD/MM/YYYY HH:mm')
                : '',
              accessControlEndTime: auth.authority.access_control_end_time
                ? moment
                    .utc(auth.authority.access_control_end_time)
                    .format('DD/MM/YYYY HH:mm')
                : '',
            })
          })
        })
      })

      return result
    },
  },
  watch: {
    async dialogCheckPermissions(value) {
      if (value) {
        await this.getAuthorityDeviceSummary()
      }
    },
    optionsHardware: {
      async handler() {
        await this.getAuthorityDeviceSummary()
      },
      deep: true,
    },
  },
  async created() {
    try {
      const { company_uuid } = this.$store.state.authentication.company

      this.loadingFilterArea = true

      const payload = {
        company_uuid,
        params: {
          dpc: 'access-authority-summary-to-list-area',
        },
      }

      await this.fetchAreas(payload)
    } catch (error) {
      const snackbar = {
        show: true,
        color: 'error',
        timeout: -1,
        message: error?.response?.data?.message || 'Something went wrong!',
      }
      this.$store.commit('SET_SNACKBAR', snackbar)
    } finally {
      this.loadingFilterArea = false
    }
  },
  methods: {
    ...mapActions({
      fetchAuthorityDeviceSummary:
        'accessAuthority/fetchAuthorityDeviceSummary',
      fetchAreas: 'area/fetchAreas',
      deleteAuthorityEmployeeMember:
        'accessAuthority/deleteAuthorityEmployeeMember',
      fetchAccessAuthority: 'accessAuthority/fetchAccessAuthority',
    }),
    onDeleteEmployee(item) {
      this.confirmDeleteEmployee = true
      this.confirmDeleteEmployeeData = {
        title: 'ยืนยันการลบ',
        text: `คุณต้องการลบพนักงาน`,
        value: `${item.employeeCode} - ${item.employeeName}`,
        item: { data: { ...item } },
      }
    },
    cancelConfirmDeleteEmployee() {
      this.confirmDeleteEmployee = false
    },
    async submitConfirmDeleteEmployee({ data }) {
      try {
        const { company_uuid } = this.$store.state.authentication.company

        this.confirmDeleteEmployeeLoading = true

        await this.deleteAuthorityEmployeeMember({
          company_uuid,
          authority_uuid: data.authority_uuid,
          body: {
            employee_uuid: data.employee_uuid,
          },
        })

        const accessAuthority = await this.fetchAccessAuthority({
          company_uuid,
          authority_uuid: data.authority_uuid,
        })

        this.$store.commit(
          'accessAuthority/EDIT_ACCESS_AUTHORITY',
          accessAuthority
        )

        this.optionsHardware.page = 1
        this.getAuthorityDeviceSummary()

        const snackbar = {
          show: true,
          color: 'success',
          timeout: 4000,
          message: 'ลบข้อมูลสำเร็จ',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      } catch (error) {
        const snackbar = {
          show: true,
          color: 'error',
          timeout: -1,
          message: error?.response?.data?.message || 'Something went wrong!',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      } finally {
        this.confirmDeleteEmployeeLoading = false
        this.confirmDeleteEmployee = false
      }
    },
    handleRefresh() {
      this.optionsHardware.page = 1
      this.getAuthorityDeviceSummary()
    },
    handleSearch() {
      this.loading = true
      this.getAuthorityDeviceSummaryBySearch()
    },
    handleFilterArea() {
      this.optionsHardware.page = 1
      this.getAuthorityDeviceSummary()
    },
    getAuthorityDeviceSummaryBySearch: _.debounce(function () {
      this.optionsHardware.page = 1
      this.getAuthorityDeviceSummary()
    }, 1000),
    async getAuthorityDeviceSummary() {
      try {
        this.loading = true

        const { sortBy, sortDesc, page, itemsPerPage } = this.optionsHardware

        let sort = ''
        const sortByKey = sortBy[0] || ''

        if (sortByKey === 'hardwareCode') sort = 'hardware_code'

        if (sortDesc.length > 0) {
          if (sortDesc[0]) {
            sort += `,desc`
          } else {
            sort += `,asc`
          }
        }

        const { company_uuid } = this.$store.state.authentication.company

        const payload = {
          company_uuid,
          params: {
            // dpc: '',
            search: this.search || undefined,
            area_uuid: this.filterArea || undefined,
            sort,
            limit: `${itemsPerPage},${(page - 1) * itemsPerPage}`,
          },
        }

        await this.fetchAuthorityDeviceSummary(payload)
      } catch (error) {
        const snackbar = {
          show: true,
          color: 'error',
          timeout: -1,
          message: error?.response?.data?.message || 'Something went wrong!',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.with-borders >>> table,
.with-borders >>> th,
.with-borders >>> td {
  border: 1px solid #ccc;
  border-collapse: collapse;
}

.with-borders >>> th,
.with-borders >>> td {
  padding: 8px;
}
</style>
